import { create } from 'zustand';
import { registerSW } from 'virtual:pwa-register';

/**
 * Non-negotiable §2 — never auto-reload.
 *
 * A new service worker installs silently and then waits. Nothing here activates
 * it: `installUpdate()` is called only from the caregiver's explicit
 * "Update installieren" tap in Betreuer-Modus (1.13). An app that reloads itself
 * mid-sentence destroys an utterance that may have taken a minute to type, and it
 * does so at the exact moment the person was about to be heard.
 */
interface UpdateState {
  waiting: boolean;
  offlineReady: boolean;
  installUpdate: () => Promise<void>;
}

let applyUpdate: ((reload?: boolean) => Promise<void>) | null = null;

export const useUpdateStore = create<UpdateState>((set) => ({
  waiting: false,
  offlineReady: false,
  installUpdate: async () => {
    if (!applyUpdate) return;
    // `true` activates the waiting worker and reloads — the one reload the app is
    // allowed to perform, because a human just asked for it.
    await applyUpdate(true);
    set({ waiting: false });
  },
}));

export function initServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  applyUpdate = registerSW({
    immediate: true,
    onNeedRefresh() {
      useUpdateStore.setState({ waiting: true });
    },
    onOfflineReady() {
      useUpdateStore.setState({ offlineReady: true });
    },
    onRegisterError(error) {
      console.error('[sw] registration failed', error);
    },
  });
}
